'use strict';

import { CONFIG } from './config.js';
import {
	DropdownTriggerManager,
	EventsManager,
	FormSyncManager,
	hydrateSearchFilterForms,
	KeyboardManager,
	ModalManager,
	SearchManager,
	URLManager,
	ValidationManager,
} from '../managers/index.js';
import { readSelections } from '../utils/url-dom.js';
import { CACHE_KEYS } from '../../geolocation-detection/constants.js';
import { getCachedValue } from '../../geolocation-detection/get-cached-value.js';
import { whenGeolocationReady } from '../../geolocation-detection/get-when-ready.js';

export default class SearchFilterForm {
	constructor() {
		this.activeModals = new Set();
		this.relationMaps = null;
		this.forms = document.querySelectorAll(CONFIG.ELEMENTS.form);

		this.searchManager = new SearchManager();
		this.modalManager = new ModalManager(this.activeModals, this.searchManager);
		this.validation = new ValidationManager(this.forms);

		// Opening a dropdown with the keyboard reports the same analytics event as
		// opening it with a click, so the two routes stay comparable.
		this.keyboardManager = new KeyboardManager(this.modalManager, {
			trackDropdownOpen: (trigger) =>
				this.searchManager.trackModalTriggerClick(trigger),
		});

		this.formSyncManager = new FormSyncManager(this.forms, this.validation, {
			applyCrossFiltering: this.applyCrossFiltering.bind(this),
			resetCrossFiltering: this.resetCrossFiltering.bind(this),
		});

		this.dropdownTriggerManager = new DropdownTriggerManager(
			this.forms,
			this.formSyncManager,
		);

		this.urlManager = new URLManager(this.forms, {
			updateDropdownLabel: this.formSyncManager.updateDropdownLabel.bind(
				this.formSyncManager,
			),
			triggerCrossFiltering: this.applyCrossFiltering.bind(this),
			syncCheckboxToAllForms: this.formSyncManager.syncCheckboxToAllForms.bind(
				this.formSyncManager,
			),
			syncRadioToAllForms: this.formSyncManager.syncRadioToAllForms.bind(
				this.formSyncManager,
			),
		});

		this.eventsManager = new EventsManager(
			{
				blur: (e) => this.searchManager.handleBlur(e),
				checkboxChange: this.handleCheckboxChange.bind(this),
				clickOutside: this.handleClickOutside.bind(this),
				closeButton: this.handleCloseClick.bind(this),
				deselectButton: this.handleDeselectClick.bind(this),
				formSubmit: this.handleFormSubmit.bind(this),
				input: (e) => this.searchManager.handleInput(e),
				keydown: (e) => this.keyboardManager.handleKeydown(e),
				modalTrigger: this.handleModalTriggerClick.bind(this),
				inputClick: this.handleInputClick.bind(this),
				radioChange: this.handleRadioChange.bind(this),
				tabbedOption: this.handleTabbedOptionClick.bind(this),
			},
			this.forms,
		);

		this.init();
	}

	init() {
		hydrateSearchFilterForms();
		this.loadRelationMapsFromPayload();
		this.urlManager.populateFromUrlParams();
		this.prefillStateWhenReady();
		this.eventsManager.bind();
	}

	/**
	 * Load payorsByState / statesByPayor from the first embedded JSON payload (shared across forms).
	 *
	 * @return {void}
	 */
	loadRelationMapsFromPayload() {
		const script = document.querySelector(
			'form[data-search-filter-form] .grow-search-filter-form__payload',
		);

		if (!script) {
			return;
		}

		try {
			const payload = JSON.parse(script.textContent || '');
			if (payload?.relations) {
				this.relationMaps = payload.relations;
			}
		} catch {
			this.relationMaps = null;
		}
	}

	/**
	 * Prefills state from cache once geolocation data is ready (from cache or after fetch).
	 *
	 * @return {void}
	 */
	prefillStateWhenReady() {
		whenGeolocationReady().then(() => this.prefillStateFromCache());
	}

	/**
	 * If there are no state parameters on page load, prefill the state from the cache.
	 *
	 * @return {void}
	 */
	prefillStateFromCache() {
		const urlParams = new URLSearchParams(window.location.search);
		if (urlParams.get('state')) {
			return;
		}

		const state = getCachedValue(
			CACHE_KEYS.state.key,
			CACHE_KEYS.state.expiryKey,
		);
		if (!state) {
			return;
		}

		this.setStateFromCache(state);
	}

	cleanup() {
		this.searchManager.cleanup();
	}

	destroy() {
		this.eventsManager.unbind();
		this.searchManager.cleanup();
		this.dropdownTriggerManager.destroy();
	}

	handleCheckboxChange(e) {
		const { target: checkbox } = e;

		if (!checkbox.matches(CONFIG.ELEMENTS.checkboxSingleSelect)) {
			this.formSyncManager.updateDropdownLabel(checkbox);
			if (checkbox.checked) {
				this.formSyncManager.syncCheckboxToAllForms(checkbox);
			} else {
				this.formSyncManager.syncCheckboxDeselectionToAllForms(checkbox);
			}
			this.searchManager.trackFilterUpdate(checkbox);
			return;
		}

		const { name } = checkbox;
		const modal = checkbox.closest(CONFIG.ELEMENTS.dropdownModal);

		if (!modal || !name) {
			// eslint-disable-next-line no-console
			console.warn('Required elements not found for single-select behavior', {
				modal,
				name,
			});
			return;
		}

		if (checkbox.checked) {
			this.handleSingleSelectCheck(checkbox, modal);
			this.searchManager.trackFilterUpdate(checkbox);
		} else {
			this.handleSingleSelectUncheck(checkbox);
		}
	}

	handleSingleSelectCheck(checkbox, modal) {
		const { name } = checkbox;
		const selector = `input[name="${name}"]:not(#${checkbox.id})`;
		const otherCheckboxes = modal.querySelectorAll(selector);

		otherCheckboxes.forEach((otherCheckbox) => {
			this.formSyncManager.syncCheckboxDeselectionToAllForms(otherCheckbox);
		});

		const currentOption = checkbox.closest(CONFIG.ELEMENTS.option);
		if (currentOption) {
			currentOption.classList.add(CONFIG.CSS_CLASSES.optionSelected);
		}

		this.modalManager.close(modal);
		this.formSyncManager.updateDropdownLabel(checkbox);
		this.formSyncManager.syncCheckboxToAllForms(checkbox);
	}

	/**
	 * Keep a single-select choice selected when its own row is toggled off.
	 *
	 * A single-select row only ever selects; the row's deselect (×) button is what
	 * clears it, and that route unchecks programmatically in handleDeselectClick
	 * without emitting a change event. So every change event that reaches here is
	 * the row itself being toggled off — by a click, Space, or Enter — and gets
	 * reverted.
	 *
	 * @param {HTMLInputElement} checkbox - The single-select checkbox.
	 * @return {void}
	 */
	handleSingleSelectUncheck(checkbox) {
		checkbox.checked = true;
		this.formSyncManager.updateDropdownLabel(checkbox);
		this.formSyncManager.syncCheckboxToAllForms(checkbox);
	}

	handleDeselectClick(e) {
		const deselectButton = e.target.closest(CONFIG.ELEMENTS.deselectButton);
		if (!deselectButton) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();

		const option = deselectButton.closest(CONFIG.ELEMENTS.option);
		const checkbox = option?.querySelector('input[type="checkbox"]');

		if (!checkbox) {
			return;
		}

		// Read this before unchecking: the button is only displayed while the row is
		// selected, so clearing the row is what takes it out of the document flow.
		const hadFocus = deselectButton.contains(
			deselectButton.ownerDocument.activeElement,
		);

		this.formSyncManager.updateDropdownLabel(checkbox);
		this.formSyncManager.syncCheckboxDeselectionToAllForms(checkbox);

		// Focus would otherwise land on <body>, outside the form the keydown listener
		// is bound to, leaving the open dropdown with no arrow keys and no Escape.
		if (hadFocus) {
			checkbox.focus();
		}
	}

	handleCloseClick(e) {
		const closeButton = e.target.closest(CONFIG.ELEMENTS.closeButton);
		if (!closeButton) {
			return;
		}

		const modal = closeButton.closest(CONFIG.ELEMENTS.dropdownModal);
		if (modal) {
			this.modalManager.close(modal);
		}
	}

	handleClickOutside(e) {
		for (const modal of this.activeModals) {
			if (!modal.contains(e.target)) {
				this.modalManager.close(modal);
			}
		}
	}

	/**
	 * If an input element is multi-select, we prevent clickOutside from firing.
	 *
	 * @param {Event} e - The click event
	 * @return {void}
	 */
	handleInputClick(e) {
		const input = e.target.closest(CONFIG.ELEMENTS.input);
		if (!input) {
			return;
		}

		this.searchManager.trackInputClick(input);

		const isMultiSelect = input.classList.contains(
			CONFIG.CSS_CLASSES.multiSelectInput,
		);

		if (!isMultiSelect) {
			this.handleClickOutside(e);
		}
	}

	/**
	 * Sets state checkbox from cache
	 * Finds and checks the state checkbox matching the detected state code
	 * @param {string} stateCode - State abbreviation (e.g., "CA", "NY")
	 */
	setStateFromCache(stateCode) {
		if (!stateCode) {
			return;
		}

		const stateCheckboxes = document.querySelectorAll(
			'input[name="states-options"]',
		);
		if (stateCheckboxes.length === 0) {
			return;
		}

		for (const checkbox of stateCheckboxes) {
			const searchData = checkbox.dataset.searchData;
			if (!searchData) {
				continue;
			}

			try {
				const data = JSON.parse(searchData);
				if (data.abbreviation === stateCode || data.label === stateCode) {
					requestAnimationFrame(() => {
						checkbox.checked = true;

						if (checkbox.matches(CONFIG.ELEMENTS.checkboxSingleSelect)) {
							const option = checkbox.closest(CONFIG.ELEMENTS.option);
							if (option) {
								option.classList.add(CONFIG.CSS_CLASSES.optionSelected);
							}
						}

						this.formSyncManager.updateDropdownLabel(checkbox);
						if (this.formSyncManager.syncCheckboxToAllForms) {
							this.formSyncManager.syncCheckboxToAllForms(checkbox);
						}
					});
					return;
				}
			} catch (error) {
				// eslint-disable-next-line no-console
				console.warn('[IP Detection] Error parsing search data:', error);
			}
		}
	}

	/**
	 * Applies cross-filtering logic between insurance and state selections across all forms.
	 *
	 * Business Requirement:
	 * - When a user selects an insurance provider, it filters available states to only show
	 *   states where that insurance is accepted
	 * - When a user selects a state, it filters available insurance providers to only show
	 *   providers that operate in that state
	 * - This ensures users can only select valid insurance-state combinations
	 *
	 * @example
	 * User selects "Blue Cross Blue Shield" → Only states where BCBS operates are shown
	 * User selects "California" → Only insurance providers operating in CA are shown
	 */
	applyCrossFiltering() {
		let globalSelectedState = null;
		let globalSelectedInsurance = null;

		for (const form of this.forms) {
			if (!globalSelectedState) {
				globalSelectedState = form.querySelector(
					'input[name="states-options"]:checked',
				);
			}
			if (!globalSelectedInsurance) {
				globalSelectedInsurance = form.querySelector(
					'input[name="payors-options"]:checked',
				);
			}
			if (globalSelectedState && globalSelectedInsurance) {
				break;
			}
		}

		if (globalSelectedState) {
			this.filterOptionsByRelatedData(
				globalSelectedState,
				'relatedInsurance',
				'payors-options',
			);
		}

		if (globalSelectedInsurance) {
			this.filterOptionsByRelatedData(
				globalSelectedInsurance,
				'relatedStates',
				'states-options',
			);
		}
	}

	/**
	 * Filters dropdown options based on related data attributes to implement cross-filtering.
	 * @param {HTMLElement} selectedItem         - The selected input element with related data attributes
	 * @param {string}      dataAttribute        - The data attribute name containing related items (e.g., 'relatedInsurance', 'relatedStates')
	 * @param {string}      targetModalInputName - The name attribute of target inputs to filter (e.g., 'payors-options', 'states-options')
	 */
	filterOptionsByRelatedData(
		selectedItem,
		dataAttribute,
		targetModalInputName,
	) {
		if (
			!this.relationMaps?.payorsByState ||
			!this.relationMaps?.statesByPayor
		) {
			return;
		}

		const selectedValue = selectedItem.value;
		let itemValues = null;

		if (dataAttribute === 'relatedInsurance') {
			const list = this.relationMaps.payorsByState[selectedValue] || [];
			itemValues = new Set(
				list.map((item) => item.value || item.id).filter(Boolean),
			);
		} else if (dataAttribute === 'relatedStates') {
			const list = this.relationMaps.statesByPayor[selectedValue] || [];
			itemValues = new Set(
				list.map((item) => item.value || item.id).filter(Boolean),
			);
		}

		if (!itemValues) {
			return;
		}

		const targetInputs = document.querySelectorAll(
			`input[name="${targetModalInputName}"]`,
		);

		targetInputs.forEach((input) => {
			const modal = input.closest(CONFIG.ELEMENTS.dropdownModal);
			if (!modal) {
				return;
			}

			const options = modal.querySelectorAll(CONFIG.ELEMENTS.option);

			options.forEach((option) => {
				const checkbox = option.querySelector('input[type="checkbox"]');
				if (!checkbox) {
					return;
				}

				const isAvailable = itemValues.has(checkbox.value);
				option.classList.toggle(CONFIG.CSS_CLASSES.optionHidden, !isAvailable);
			});
		});
	}

	resetCrossFiltering(checkbox) {
		const { name } = checkbox;

		const crossFilterMap = {
			'states-options': 'payors-options',
			'payors-options': 'states-options',
		};

		const targetModalName = crossFilterMap[name];
		if (!targetModalName) {
			return;
		}

		const targetInputs = document.querySelectorAll(
			`input[name="${targetModalName}"]`,
		);

		targetInputs.forEach((input) => {
			const modal = input.closest(CONFIG.ELEMENTS.dropdownModal);
			if (modal) {
				const options = modal.querySelectorAll(CONFIG.ELEMENTS.option);
				options.forEach((option) => {
					option.classList.remove(CONFIG.CSS_CLASSES.optionHidden);
				});
			}
		});
	}

	handleFormSubmit(e) {
		e.preventDefault();

		const form = e.target;
		this.validation.clearAllErrors(form);

		if (!this.validation.validateRequiredFields(form)) {
			return false;
		}

		this.searchManager.trackFormSubmit(form);

		// Read the form once and reuse the selections for both the history write and the
		// param build, so we don't sweep the DOM twice per submit.
		const selections = readSelections(form);

		// Record the active filters in history before redirecting, then build the URL.
		this.urlManager.commitFilterHistory(form, selections);
		const searchParams = this.urlManager.buildSearchParams(form, selections);
		const searchUrl = this.urlManager.buildSearchUrl(searchParams);

		window.location.href = searchUrl;
	}

	/**
	 * Handle modal trigger click - both open modal and track analytics
	 * @param {Event} e - The click event
	 */
	handleModalTriggerClick(e) {
		this.searchManager.trackModalTriggerClick(e.target);
		this.modalManager.handleModalOpen(e);
	}

	/**
	 * Report a press of a tabbed option (Type of Care) for analytics.
	 *
	 * Selection sync and the filter-update event run off `change` in
	 * handleRadioChange instead, so they cover keyboard selection too. This
	 * handler reports only the tab press itself, which is click-shaped.
	 *
	 * @param {Event} e - The click event
	 * @return {void}
	 */
	handleTabbedOptionClick(e) {
		// Clicking the label forwards a second, synthetic click to its radio, so a
		// single press can reach this handler twice. Count only the radio's, which
		// every route produces exactly once.
		const radio = e.target.closest('input[type="radio"]');
		const tabbedOption = radio?.closest(CONFIG.ELEMENTS.tabbedOption);
		if (!tabbedOption) {
			return;
		}

		this.searchManager.trackTabbedOptionClick(tabbedOption);
	}

	/**
	 * Sync a radio selection across every form instance and report the filter
	 * update. Today the only radios in the form are the tabbed Type of Care
	 * inputs.
	 *
	 * Bound to `change` rather than `click` because a radio group's arrow keys do
	 * not reliably emit a click, which left keyboard selection out of sync with
	 * the other form instances on the page.
	 *
	 * @param {Event} e - The change event
	 * @return {void}
	 */
	handleRadioChange(e) {
		const radio = e.target;
		if (!radio.checked) {
			return;
		}

		this.searchManager.trackFilterUpdate(radio);
		this.formSyncManager.syncRadioToAllForms(radio);
	}
}
